import utils from './utils';
import {
  COLLAPSED,
  COLLAPSED_AS_ROUNDED,
  EXPANDED,
} from '../constants/widget-size';
import { MEMBERS_APP_ID } from '../constants/apps-ids';

declare const window: any;

export default {
  navigateToSection(sectionIdentifier, state, onFailure) {
    return window.Wix.Utils.navigateToSection(
      sectionIdentifier,
      state,
      onFailure,
    );
  },
  getLocale() {
    return window.Wix.Utils.getLocale();
  },

  getCompId() {
    return window.Wix.Utils.getCompId();
  },

  getDeviceType() {
    return window.Wix.Utils.getDeviceType();
  },

  colorationDecision({ userChoice, addPanelColor, defaultColor, color1 }) {
    if (userChoice) {
      return userChoice;
    }
    const color = addPanelColor || defaultColor;
    if (color) {
      if (utils.isWhite(color)) {
        return color1;
      }
      return color;
    }
    return '';
  },

  getColorByParam(param) {
    return window.Wix.Styles.getStyleColorByKey(param);
  },

  getStyleParams(): Promise<{ fonts: any; colors: any; booleans: any }> {
    return new Promise(resolve => {
      window.Wix.Styles.getStyleParams(resolve);
    });
  },

  getSiteColors() {
    return new Promise(resolve => {
      window.Wix.Styles.getSiteColors(resolve);
    });
  },

  getColor5() {
    return window.Wix.Styles.getColorByreference('color-5').value;
  },

  getColor1() {
    return window.Wix.Styles.getColorByreference('color-1').value;
  },

  getColor8() {
    return window.Wix.Styles.getColorByreference('color-8').value;
  },

  currentMember() {
    return new Promise(resolve => {
      window.Wix.currentMember(resolve);
    });
  },

  resizeWindow(width, height) {
    return new Promise(resolve => {
      window.Wix.resizeWindow(width, height, resolve);
    });
  },

  resizeToCollapsedMode(asRounded) {
    return asRounded
      ? this.resizeWindow(
          COLLAPSED_AS_ROUNDED.WIDTH,
          COLLAPSED_AS_ROUNDED.HEIGHT,
        )
      : this.resizeWindow(COLLAPSED.WIDTH, COLLAPSED.HEIGHT);
  },

  resizeToExpandedMode() {
    return this.resizeWindow(EXPANDED.WIDTH, EXPANDED.HEIGHT);
  },

  resizeToHidden() {
    return this.resizeWindow(0, 0);
  },

  getPublicData(setting, onPublicDataChanged, setDefaultSettings?) {
    return new Promise(resolve => {
      window.Wix.Data.Public.get(
        setting,
        { scope: 'APP' },
        res => {
          onPublicDataChanged(res);
          resolve();
        },
        () => {
          setDefaultSettings && setDefaultSettings(setting);
          resolve();
        },
      );
    });
  },

  setDataPublic(tab, data) {
    return new Promise((resolve, reject) => {
      window.Wix.Data.Public.set(tab, data, { scope: 'APP' }, resolve, reject);
    });
  },

  navigateToMemberProfile(memberId) {
    window.Wix.getPublicAPI(
      { appDefinitionId: MEMBERS_APP_ID },
      api => {
        api
          .navigateToMember({
            memberId,
          })
          .catch(console.error);
      },
      console.error,
    );
  },

  getResizedImgUrl(relativeUrl, width, height) {
    return window.Wix.Utils.Media.getResizedImageUrl(
      relativeUrl,
      width,
      height,
    );
  },

  getViewMode() {
    return window.Wix.Utils.getViewMode();
  },

  isInTemplate() {
    return window.Wix.Utils.getDemoMode();
  },

  getMetaSiteId() {
    return this.getInstanceValue('metaSiteId');
  },

  urlContainsQuery(query): Promise<boolean> {
    return new Promise(resolve => {
      window.Wix.Settings.getSiteInfo(({ url }) =>
        resolve(url && url.indexOf(query) > -1),
      );
    });
  },

  getUserSessionToken() {
    return new Promise(resolve => {
      window.Wix.Activities.getUserSessionToken(resolve);
    });
  },

  getInstanceId() {
    return window.Wix.Utils.getInstanceId();
  },

  getInstanceValue(name: string) {
    return window.Wix.Utils.getInstanceValue(name);
  },

  syncPageTitle(handler) {
    window.Wix.Settings.getSiteInfo(siteInfo => {
      if (window.__EXPERIMENTS__['specs.com.SDKPageTitleOnly'] === 'true') {
        handler(siteInfo.pageTitleOnly);
      } else {
        const pipeIndex: number = siteInfo.pageTitle.indexOf(' | ');
        let title;

        if (pipeIndex === -1) {
          title = siteInfo.pageTitle;
        } else {
          title = siteInfo.pageTitle.substr(pipeIndex + 2).trim();
        }

        handler(title);
      }
    });
  },

  getInstance() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('instance') || '';
  },

  openModal(url, width, height) {
    window.Wix.openModal(url, width, height, undefined, window.Wix.Theme.BARE);
  },

  closeModal() {
    window.Wix.closeWindow();
  },

  subscribeForStartPrivateChat(handler) {
    window.Wix.PubSub.subscribe('ChatWidget.startPrivateChat', handler, false);
  },

  publishToPubSub(event, data) {
    window.Wix.PubSub.publish(event, data, true);
  },

  subscribeForComponentDeleted(handler) {
    window.Wix.addEventListener(window.Wix.Events.COMPONENT_DELETED, handler);
  },

  subscribeForPublicDataChanged(handler) {
    window.Wix.addEventListener(window.Wix.Events.PUBLIC_DATA_CHANGED, handler);
  },

  subscribeForSettingsUpdated(handler) {
    window.Wix.addEventListener(window.Wix.Events.SETTINGS_UPDATED, handler);
  },

  subscribeForEditModeChanged(handler) {
    window.Wix.addEventListener(window.Wix.Events.EDIT_MODE_CHANGE, handler);
  },

  subscribeForSitePublished(handler) {
    window.Wix.addEventListener(window.Wix.Events.SITE_PUBLISHED, handler);
  },

  subscribeForThemeChanged(handler) {
    window.Wix.addEventListener(window.Wix.Events.THEME_CHANGE, handler);
  },

  subscribeForNavigation(handler) {
    window.Wix.addEventListener(window.Wix.Events.PAGE_NAVIGATION, () => {
      this.syncPageTitle(handler);
    });
  },

  subscribeForMetadataChange(handler) {
    window.Wix.addEventListener(window.Wix.Events.SITE_METADATA_CHANGED, () => {
      this.syncPageTitle(handler);
    });
  },

  subscribeForSettingsClosed(handler) {
    window.Wix.addEventListener(window.Wix.Events.EDITOR_EVENT, handler);
  },

  subscribeToExpandWidgetRequest(handler) {
    window.Wix.PubSub.subscribe('ChatWidget.expandWidget', handler, true);
  },

  updateQab({
    visible,
    notifications,
    color,
  }: {
    visible: boolean;
    notifications: boolean;
    color?: string;
  }) {
    window.Wix.Mobile.setMobileActionBarButton({
      visible,
      notifications,
      ...(color ? { color } : {}),
    });
  },

  getBiToken() {
    return this.getInstanceValue('biToken');
  },

  getAid() {
    return this.getInstanceValue('aid');
  },

  registerQab(callback) {
    window.Wix.addEventListener(
      window.Wix.Events.QUICK_ACTION_TRIGGERED,
      callback,
    );
  },

  showMobileComponent() {
    window.Wix.Mobile.showFullscreen();
  },

  hideMobileComponent() {
    window.Wix.Mobile.hideFullscreen();
  },

  setBrowserTabTitle(options, onSuccess?, onFailure?) {
    window.Wix.setBrowserTabTitle(options, onSuccess, onFailure);
  },

  onReady(callback: Function) {
    window.Wix.onReady(callback);
  },

  reportApplicationLoaded() {
    window.Wix.Performance.applicationLoaded();
  },
};
